import React, { useState, useEffect } from "react"
import { List, useTheme } from "@material-ui/core"
import { useParams, useHistory, useLocation } from "react-router-dom"
import { useApolloClient } from "@apollo/client"
import { SubNavigationListItem, NoItemsMessage, ActionList } from "../../components"
import { queryAction, useLazyQueryActions, useMutationDeleteAction, useQueryActionCounts } from "../../data"
import { toId } from "../../utils"
import { useAuth } from "../../services"
import { useLazyQueryAction } from "../../data/actions/useLazyQueryAction"

const Actions = ({ onMenuClick }) => {
  const history = useHistory()
  const client = useApolloClient()
  const [deleteAction] = useMutationDeleteAction()

  const [editAction, setEditAction] = useState(null)
  const [loadActions, actionsResult] = useLazyQueryActions()
  const [loadAction, actionResult] = useLazyQueryAction()
  const [unsubscribes, setUnsubscribes] = useState(null)
  const { set, id, action } = useParams()

  const isSingle = set === "action"
  const isSingleDeleted = isSingle && id === "null"
  const baseUri = `/hub/actions/${set}`

  const result = isSingle ? actionResult : actionsResult

  useEffect(() => {
    if (isSingleDeleted) return
    if (isSingle) {
      loadAction({ variables: { id } })
    } else {
      loadActions({ variables: { set } })
    }
  }, [isSingle, id, loadActions, loadAction, isSingleDeleted, set])

  useEffect(() => {
    if (isSingle && !editAction && id && action === "edit" && actionResult.data)
      setEditAction(actionResult.data.action.items[0])
  }, [action, editAction, id, isSingle, actionResult.data])

  useEffect(() => {
    if (!isSingle) {
      if (!editAction && id && action === "edit" && actionsResult.data) {
        const item = actionsResult.data.actions.items.find((a) => toId(a) === id)
        if (!item) {
          const updateLoadAction = async () => {
            const loadItem = await queryAction(id, client)
            if (loadItem && loadItem.data) {
              setEditAction(loadItem.data.actions.items[0])
            }
          }
          updateLoadAction()
        } else setEditAction(item)
      } else if (!id && editAction) {
        setEditAction(null)
      }
    }
  }, [action, client, editAction, id, isSingle, actionsResult.data])

  useEffect(() => {
    if (isSingle && actionResult.error) history.push("/hub/all")
  })

  useEffect(() => {
    if (editAction && action !== "edit") setEditAction(null)
  }, [action, editAction])

  useEffect(() => {
    const { called, subscribe } = actionsResult
    if (called && !unsubscribes) {
      setUnsubscribes(subscribe())
      return () => {
        if (unsubscribes) {
          unsubscribes.map((unsub) => unsub())
        }
      }
    }
  }, [actionsResult, unsubscribes])

  useEffect(() => {
    const { called, subscribe } = actionResult
    if (called && !unsubscribes) {
      setUnsubscribes(subscribe())
      return () => {
        if (unsubscribes) {
          unsubscribes.map((unsub) => unsub())
        }
      }
    }
  }, [actionResult, unsubscribes])
  const handleEditAction = (post) => {
    history.push(`${baseUri}/${toId(post)}/edit`)
  }
  const handleDeleteAction = async ({ id: deleteId }) => {
    await deleteAction({
      variables: {
        id: deleteId,
      },
    })
    loadActions()
  }

  const handleToggleSubNav = (event) => {
    onMenuClick && onMenuClick(event)
  }

  const getTitle = () => {
    switch (set) {
      case "unresolved":
        return "Unresolved actions"
      case "resolved":
        return "Resolved actions"
      default:
        return "Unrecognised set"
    }
  }

  return (
    <>
      {!isSingleDeleted && (
        <ActionList
          title={getTitle()}
          actions={actionsResult.data?.actions}
          {...result}
          onEdit={handleEditAction}
          onDelete={handleDeleteAction}
          onOpenSubNav={handleToggleSubNav}
        />
      )}
      {isSingleDeleted && <NoItemsMessage>This action has been removed</NoItemsMessage>}
    </>
  )
}

const ActionsSubNav = ({ onClick }) => {
  const theme = useTheme()
  const { hasFeature } = useAuth()
  const location = useLocation()
  const actionCountsResult = useQueryActionCounts()
  const [unsubscribes, setUnsubscribes] = useState(null)

  useEffect(() => {
    const { called, subscribe } = actionCountsResult
    if (called && !unsubscribes) {
      setUnsubscribes(subscribe())
      return () => {
        if (unsubscribes) {
          unsubscribes.map((unsub) => unsub())
        }
      }
    }
  }, [actionCountsResult, unsubscribes])

  const handleClick = () => {
    onClick && onClick()
  }

  if (!hasFeature("actions")) return null

  return (
    <List>
      <SubNavigationListItem
        to="/hub/actions/unresolved"
        text="Unresolved"
        badgeCount={actionCountsResult.data?.actionCounts.unresolved || 0}
        badgeStyleProps={{ color: "white", backgroundColor: theme.palette.unconfirmed.main }}
        active={location.pathname.startsWith("/hub/actions/unresolved")}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/hub/actions/resolved"
        text="Resolved"
        active={location.pathname.startsWith("/hub/actions/resolved")}
        onClick={handleClick}
      />
    </List>
  )
}

export { Actions, ActionsSubNav }
